import React, { useEffect, useState } from "react";
import SessionLoader from "./sessionLoader";
import { ChatContainer } from "../liveChat";
import { useSelector } from "react-redux";

function CountdownTimer({ eventTime, link, sessionId, isEnded, sessionData }) {
  const userStore = useSelector((state) => state.userReducer);

  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(eventTime));
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const remaining = getTimeRemaining(eventTime);

    // 🔹 Case 1: Event is in the future
    if (remaining.total > 0) {
      // show loader for 1s, then show countdown
      const initLoader = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(initLoader);
    }

    // 🔹 Case 2: Event is already live
    setIsLive(true);
    const initLoader = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(initLoader);
  }, [eventTime]);

  // 🔹 Countdown logic when event is in future
  useEffect(() => {
    if (loading || isLive) return;

    const timer = setInterval(() => {
      const remaining = getTimeRemaining(eventTime);
      setTimeLeft(remaining);

      if (remaining.total <= 0) {
        setLoading(true);
        clearInterval(timer);

        setTimeout(() => {
          setIsLive(true);
          setLoading(false);
        }, 1000);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [eventTime, loading, isLive]);

  const isLatest = sessionData[0]?._id === sessionId;
  // 🔹 Show loader at zero time
  if (loading) {
    return <SessionLoader />;
  }

  // 🔹 When live — only show link or "Live Now!"
  if (isLive) {
    return (
      <div className="msg mt-2">
        {link ? (
          <>
            {!playing ? (
              <div
                onClick={() => setPlaying(true)}
                style={{
                  width: '100%',
                  paddingBottom: '56.25%',
                  position: 'relative',
                  backgroundColor: '#111111',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'var(--theme-gradient)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    boxShadow: '0 4px 15px 0 rgba(116,79,168,0.5)',
                  }}>
                    <i className="fa-solid fa-play" style={{ color: '#fff', fontSize: 22, marginLeft: 3 }} />
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(245,245,245,0.5)', letterSpacing: '0.5px' }}>Click To Watch</span>
                </div>
              </div>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: link,
                }}
              />
            )}

            {isLatest && (
              <>
                {!isEnded && (
                  <div className="live-badge">
                    <span className="live-dot" />
                    LIVE
                  </div>
                )}
                <div className="openChat" onClick={() => setShowChat(true)}>
                  Chat
                </div>
                {showChat && (
                  <ChatContainer
                    sessionId={sessionId}
                    userToken={userStore?.users?.token}
                    setShowChat={setShowChat}
                    isEnded={isEnded}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <span className="text-success fw-bold fs-5">Live Now!</span>
        )}
      </div>
    );
  }

  // 🔹 Countdown display before event
  return (
    <div className="sci-fi-timer-container">
      <div className="project-launch-info">
        <h3 className="project-title">LIVE SESSION WILL START IN</h3>
      </div>
      <div className="timer-segments-row">
        <div className="timer-segment-wrapper">
          <div className="timer-circle">
            <div className="timer-value">{timeLeft.days}</div>
          </div>
          <span className="timer-label">Days</span>
        </div>

        {/* Separator between segments */}
        <div className="segment-separator"></div>

        <div className="timer-segment-wrapper">
          <div className="timer-circle one">
            <div className="timer-value">{timeLeft.hours}</div>
          </div>
          <span className="timer-label">Hours</span>
        </div>

        {/* Separator between segments */}
        <div className="segment-separator"></div>

        <div className="timer-segment-wrapper">
          <div className="timer-circle two">
            <div className="timer-value">{timeLeft.minutes}</div>
          </div>
          <span className="timer-label">Minutes</span>
        </div>

        {/* Separator between segments */}
        <div className="segment-separator"></div>

        <div className="timer-segment-wrapper">
          <div className="timer-circle three">
            <div className="timer-value">{timeLeft.seconds}</div>
          </div>
          <span className="timer-label">Seconds</span>
        </div>
      </div>
    </div>
  );
}

function getTimeRemaining(eventTime) {
  eventTime = new Date(eventTime);

  const total = Date.parse(eventTime) - Date.now();
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return {
    total,
    days: days >= 0 ? days : 0,
    hours: hours >= 0 ? hours : 0,
    minutes: minutes >= 0 ? minutes : 0,
    seconds: seconds >= 0 ? seconds : 0,
  };
}

export default CountdownTimer;
